import { ReactNode, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { styled } from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { saveMyHubDemoReturnUrl } from '@/features/myHub/myHubDemo/helpers';
import { useMemberAuth } from '@/features/vfo/hooks/useMemberAuth';
import { useTokenRefreshWatch } from '@/processes/auth/authFlow/useTokenRefreshWatch';
import { isPathSafeForRedirect } from '@/shared/helpers';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useAppRedirectStore } from '@/shared/store/appRedirect.store';
import { useAuthStore } from '@/shared/store/auth.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { SpinV2 } from '@/shared/uikit/Spin';

import { MigrationErrorLockScreenProvider } from './MigrationErrorLockScreen';
import { SubscriptionLockScreenProvider } from './SubscriptionLockScreenProvider';

export const publicRoutes = [
  '',
  '/',
  '/auth/login/personal-account',
  '/auth/login',
  '/auth/login/assistant',
  '/vfo/join',
  '/test/new-form',
  'thanks',
];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const router = useAppRouter();

  const { accessToken } = useAuthStore();
  const { isVfoMember } = useMemberAuth();
  const { keyPair } = useKeyPairStore();
  const { setRedirectTo } = useAppRedirectStore();
  const [loading, setLoading] = useState(true);

  const isPublic = publicRoutes.includes(router.pathname);
  const isRoot = router.pathname === '/';
  const isEnterPin = router.pathname === '/auth/login/enter-pin-code';

  const isExportedDemo = clientConfig.demoHub.isDemoHub;

  useTokenRefreshWatch();

  const resolveWebApp = async () => {
    const needRedirect = !isPublic || !isEnterPin;
    const needUrlRestoration = !isPublic && !isEnterPin;

    if (needUrlRestoration && isPathSafeForRedirect(router.pathname)) {
      setRedirectTo(router.asPath);
    }

    if (!isPublic && !accessToken) {
      localStorage.clear();
      router.push('/');
      setLoading(false);
      return;
    }

    if (needRedirect && accessToken) {
      if (isVfoMember) {
        await router.push('/vfo/dashboard');
      } else {
        await router.push('/auth/login/enter-pin-code');
      }
    }

    setLoading(false);
  };

  const resolveMyHub = async () => {
    if (isExportedDemo) {
      saveMyHubDemoReturnUrl();
    }

    if (!keyPair || !isPathSafeForRedirect(router.pathname)) {
      await router.push('/');
    }

    setLoading(false);
  };

  const resolvePath = async () => {
    if (!clientConfig.isExported) {
      await resolveWebApp(); // Web App
    } else {
      await resolveMyHub(); // MyHub
    }
  };

  // Resolve paths
  useEffect(() => {
    resolvePath();
  }, []);

  // Show loader on root and not public paths
  if ((isRoot || !isPublic) && loading) {
    return (
      <Loader>
        <SpinV2 />
      </Loader>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientConfig.googleAuthClientId}>
      <MigrationErrorLockScreenProvider>
        <SubscriptionLockScreenProvider>
          <>{children}</>
        </SubscriptionLockScreenProvider>
      </MigrationErrorLockScreenProvider>
    </GoogleOAuthProvider>
  );
};

// TODO it should by in main layout (min-height: 100% and width: 100%)
const Loader = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
